import React, { createContext, useState, useContext, useEffect } from 'react';
import { ICurrenUserDataResponse } from 'services/authentication-api/types';
import { IGetActiveEmailProvidersResponse } from 'services/emailProvider-api/types';
import {
  ISubscription,
  SubscriptionStatus,
} from 'services/subscription-api/types';
import { IPendingTransactionResponse } from 'services/transaction-api/types';

import { UserInfo } from 'types/user';
import {
  decodeToken,
  getAuthToken,
  removeAuthToken,
  removeIsFreeTrialConsumed,
  removeNextStep,
  removeRefreshToken,
} from 'utils/token-handler';
import { CurrentSubscriptionStatus } from './types';
import { PlanCycleTypes } from 'services/pricing-api/types';

const AuthContext = createContext<{
  token: string;
  logout: () => void;
  userInfo: UserInfo;
  currentUserData: ICurrenUserDataResponse | null;
  setCurrentUserData: (val: ICurrenUserDataResponse) => void;
  currentSubscriptionData: ISubscription | null;
  setCurrentSubscriptionData: (val: ISubscription) => void;
  isCurrentUserDataLoading: boolean;
  setIsCurrentUserDataLoading: (val: boolean) => void;
  isCurrentSubscriptionDataLoading: boolean;
  setIsCurrentSubscriptionDataLoading: (val: boolean) => void;
  currentUserPendingTransactionData: IPendingTransactionResponse | null;
  setCurrentUserPendingTransactionData: (
    val: IPendingTransactionResponse
  ) => void;
  isCurrentUserPendingTransactionDataLoading: boolean;
  setIsCurrentUserPendingTransactionDataLoading: (val: boolean) => void;
  activeProviders: IGetActiveEmailProvidersResponse;
  setActiveProviders: (val: IGetActiveEmailProvidersResponse) => void;
  isActiveProvidersLoading: boolean;
  setIsActiveProvidersLoading: (val: boolean) => void;
  subscriptionCurrentStatus: CurrentSubscriptionStatus | null;
  isCheckoutOpen: boolean;
  setIsCheckoutOpen: (val: boolean) => void;
  currentCheckoutTransactionId: string | number | null;
  setCurrentCheckoutTransactionId: (val: string | number) => void;
  currentCheckoutPlanId: string | number | null;
  setCurrentCheckoutPlanId: (val: string | number) => void;
  currentCheckoutPeriod: PlanCycleTypes | null;
  setCurrentCheckoutPeriod: (val: PlanCycleTypes) => void;
} | null>(null);

export const AuthenticationContext = ({ children }) => {
  const [token, setToken] = useState<string>('');
  const [userInfo, setUserInfo] = useState<UserInfo>({});
  const [currentUserData, setCurrentUserData] =
    useState<ICurrenUserDataResponse | null>(null);
  const [isCurrentUserDataLoading, setIsCurrentUserDataLoading] =
    useState(true);
  const [currentSubscriptionData, setCurrentSubscriptionData] =
    useState<ISubscription | null>(null);
  const [
    currentUserPendingTransactionData,
    setCurrentUserPendingTransactionData,
  ] = useState<IPendingTransactionResponse | null>(null);
  const [
    isCurrentUserPendingTransactionDataLoading,
    setIsCurrentUserPendingTransactionDataLoading,
  ] = useState(true);
  const [
    isCurrentSubscriptionDataLoading,
    setIsCurrentSubscriptionDataLoading,
  ] = useState(true);
  const [activeProviders, setActiveProviders] =
    useState<IGetActiveEmailProvidersResponse | null>(null);

  const [isActiveProvidersLoading, setIsActiveProvidersLoading] =
    useState(false);

  const [subscriptionCurrentStatus, setSubscriptionCurrentStatus] =
    useState<CurrentSubscriptionStatus | null>();

  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

  const [currentCheckoutTransactionId, setCurrentCheckoutTransactionId] =
    useState<string | number | null>(null);

  const [currentCheckoutPlanId, setCurrentCheckoutPlanId] = useState<
    string | number | null
  >(null);

  const [currentCheckoutPeriod, setCurrentCheckoutPeriod] =
    useState<PlanCycleTypes | null>(null);

  useEffect(() => {
    if (currentSubscriptionData && currentUserData) {
      if (
        currentSubscriptionData.status === SubscriptionStatus.ACTIVE ||
        currentSubscriptionData.status === SubscriptionStatus.PAST_DUE ||
        currentSubscriptionData.status === SubscriptionStatus.TRIALING
      ) {
        setSubscriptionCurrentStatus(CurrentSubscriptionStatus.ACTIVE);
      } else {
        if (currentUserData && currentUserData.freeTrialConsumed === false) {
          setSubscriptionCurrentStatus(
            CurrentSubscriptionStatus.INACTIVE_TRIAL
          );
        } else {
          setSubscriptionCurrentStatus(CurrentSubscriptionStatus.INACTIVE);
        }
      }
    }
  }, [currentSubscriptionData, currentUserData]);

  useEffect(() => {
    const tokenFromLocalStorage = getAuthToken();

    const decodedToken = tokenFromLocalStorage
      ? decodeToken(tokenFromLocalStorage)
      : null;

    setUserInfo(decodedToken);
  }, []);

  const logout = () => {
    setToken(null);
    removeAuthToken();
    removeRefreshToken();
    removeNextStep();
    removeIsFreeTrialConsumed();
    window.location.replace('/login');
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        logout,
        userInfo,
        currentUserData,
        setCurrentUserData,
        currentSubscriptionData,
        setCurrentSubscriptionData,
        isCurrentUserDataLoading,
        setIsCurrentUserDataLoading,
        isCurrentSubscriptionDataLoading,
        setIsCurrentSubscriptionDataLoading,
        currentUserPendingTransactionData,
        setCurrentUserPendingTransactionData,
        isCurrentUserPendingTransactionDataLoading,
        setIsCurrentUserPendingTransactionDataLoading,
        activeProviders,
        setActiveProviders,
        isActiveProvidersLoading,
        setIsActiveProvidersLoading,
        subscriptionCurrentStatus,
        isCheckoutOpen,
        setIsCheckoutOpen,
        currentCheckoutTransactionId,
        setCurrentCheckoutTransactionId,
        currentCheckoutPlanId,
        setCurrentCheckoutPlanId,
        currentCheckoutPeriod,
        setCurrentCheckoutPeriod,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
